import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useUser } from '../UserContext';
import UserDeletionDialog from './UserDeletionDialog';
import {
  Box,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Button,
  Chip,
  Stack,
  Card,
  CardContent,
  CardActions,
  Grid,
  Avatar,
  Divider,
  FormControlLabel,
  Checkbox,
  Collapse,
  IconButton,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import { 
  Search as SearchIcon, 
  Visibility as VisibilityIcon,
  Block as BlockIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import api from '../api';

function UserSearch() {
  const navigate = useNavigate();
  const { selectUserById } = useUser();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const queryClient = useQueryClient();
  
  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    banned: null, // null = all, true = banned only, false = not banned only
    permabanned: null, // null = all, true = permabanned only, false = not permabanned only
    createdAfter: '',
    createdBefore: '',
    onlineAfter: '',
    onlineBefore: ''
  });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);
  
  // Debounce filters
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 300);

    return () => clearTimeout(timer);
  }, [filters]);

  // Infinite query for users (search or default)
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteQuery({
    queryKey: ['users', debouncedQuery, debouncedFilters],
    queryFn: async ({ pageParam = 1 }) => {
      const params = new URLSearchParams({
        page: pageParam.toString()
      });
      
      if (debouncedQuery) {
        params.append('query', debouncedQuery);
      }
      
      // Add filter parameters
      if (debouncedFilters.banned !== null) {
        params.append('banned', debouncedFilters.banned.toString());
      }
      if (debouncedFilters.permabanned !== null) {
        params.append('permabanned', debouncedFilters.permabanned.toString());
      }
      if (debouncedFilters.createdAfter) {
        params.append('createdAfter', debouncedFilters.createdAfter);
      }
      if (debouncedFilters.createdBefore) {
        params.append('createdBefore', debouncedFilters.createdBefore);
      }
      if (debouncedFilters.onlineAfter) {
        params.append('onlineAfter', debouncedFilters.onlineAfter);
      }
      if (debouncedFilters.onlineBefore) {
        params.append('onlineBefore', debouncedFilters.onlineBefore);
      }
      
      const url = `/users/search?${params.toString()}`;
      const response = await api.get(url);
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      return lastPage?.pagination?.hasMore ? lastPage.pagination.page + 1 : undefined;
    },
    staleTime: 0, // Always refetch
    cacheTime: 0, // Don't cache
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  // Flatten all users from all pages, handling potential error responses
  const users = data?.pages?.flatMap(page => {
    // Handle both possible response structures
    if (Array.isArray(page)) {
      return page; // Page is directly an array of users
    }
    return page?.users || []; // Page is an object with users property
  }) || [];
  
  // Debug logging
  console.log('React Query data:', data);
  console.log('Processed users:', users);
  console.log('Users length:', users.length);
  console.log('Is loading:', isLoading);
  console.log('Error:', error);

  // Mutation for updating user status (banned/cheater/permaban)
  const updateUserStatusMutation = useMutation({
    mutationFn: async ({ userId, field, value, updates }) => {
      // Support both single field updates and multiple field updates
      const updateData = updates || { [field]: value };
      const response = await api.put(`/users/${userId}`, updateData);
      return response.data;
    },
    onSuccess: () => {
      // Refresh the user list (infinite query)
      queryClient.invalidateQueries(['users']);
    },
  });


  const handleToggleBanned = async (user, event) => {
    event.stopPropagation();
    const newBannedValue = user.banned ? 0 : 1;
    // Ban button toggles both banned and cheater
    updateUserStatusMutation.mutate({ 
      userId: user.id, 
      updates: {
        banned: newBannedValue,
        cheater: newBannedValue
      }
    });
  };

  const handleTogglePermaban = async (user, event) => {
    event.stopPropagation();
    const newPermabanValue = user.permaban ? 0 : 1;
    
    let updates = { permaban: newPermabanValue };
    
    // Add automatic note for permaban
    if (newPermabanValue === 1) {
      const timestamp = new Date().toLocaleString();
      const permabanNote = `[${timestamp}] User permanently banned by admin.`;
      
      const currentNotes = user.notes || '';
      const newNotes = currentNotes.trim() 
        ? currentNotes + '\n\n' + permabanNote
        : permabanNote;
      
      updates.notes = newNotes;
    }
    
    updateUserStatusMutation.mutate({ 
      userId: user.id, 
      updates: updates
    });
  };

  const handleDeleteSuccess = () => {
    // Refresh the user list
    queryClient.invalidateQueries(['users']);
  };

  const handleOpenDeleteDialog = (user, event) => {
    event.stopPropagation();
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const formatMoani = (moani) => {
    if (!moani || moani === '0') return '0';
    // Convert from wei to MOANI (assuming 18 decimals)
    const moaniValue = parseFloat(moani) / Math.pow(10, 18);
    return moaniValue.toFixed(2);
  };

  // Scroll detection for infinite loading
  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const threshold = 100; // Load more when 100px from bottom
    
    if (scrollHeight - scrollTop <= clientHeight + threshold && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Filter helper functions
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      banned: null,
      permabanned: null,
      createdAfter: '',
      createdBefore: '',
      onlineAfter: '',
      onlineBefore: ''
    });
  };

  const hasActiveFilters = () => {
    return filters.banned !== null || 
           filters.permabanned !== null || 
           filters.createdAfter || 
           filters.createdBefore || 
           filters.onlineAfter || 
           filters.onlineBefore;
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        User Search
      </Typography>
      
      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by user ID, name, email, wallet, player ID, or linked wallet..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
        />
        
        {/* Filter Toggle Button */}
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setShowFilters(!showFilters)}
            color={hasActiveFilters() ? 'primary' : 'inherit'}
          >
            Filters {hasActiveFilters() && `(${Object.values(filters).filter(v => v !== null && v !== '').length})`}
          </Button>
          
          {hasActiveFilters() && (
            <Button
              variant="text"
              size="small"
              onClick={clearFilters}
              sx={{ textTransform: 'none' }}
            >
              Clear All
            </Button>
          )}
        </Box>
        
        {/* Filters Panel */}
        <Collapse in={showFilters}>
          <Box sx={{ 
            mt: 2, 
            p: 2, 
            bgcolor: 'background.paper', 
            borderRadius: 1, 
            border: '1px solid', 
            borderColor: 'divider' 
          }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Filter Options
            </Typography>
            
            <Grid container spacing={2}>
              {/* Status Filters */}
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  User Status
                </Typography>
                <Stack spacing={1}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filters.banned === true}
                        onChange={(e) => {
                          if (e.target.checked) {
                            handleFilterChange('banned', true);
                          } else {
                            handleFilterChange('banned', null);
                          }
                        }}
                      />
                    }
                    label="Show only banned users"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filters.banned === false}
                        onChange={(e) => {
                          if (e.target.checked) {
                            handleFilterChange('banned', false);
                          } else {
                            handleFilterChange('banned', null);
                          }
                        }}
                      />
                    }
                    label="Show only non-banned users"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filters.permabanned === true}
                        onChange={(e) => {
                          if (e.target.checked) {
                            handleFilterChange('permabanned', true);
                          } else {
                            handleFilterChange('permabanned', null);
                          }
                        }}
                      />
                    }
                    label="Show only permabanned users"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filters.permabanned === false}
                        onChange={(e) => {
                          if (e.target.checked) {
                            handleFilterChange('permabanned', false);
                          } else {
                            handleFilterChange('permabanned', null);
                          }
                        }}
                      />
                    }
                    label="Show only non-permabanned users"
                  />
                </Stack>
              </Grid>
              
              {/* Date Filters */}
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Date Filters
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Created After"
                    type="datetime-local"
                    size="small"
                    value={filters.createdAfter}
                    onChange={(e) => handleFilterChange('createdAfter', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Created Before"
                    type="datetime-local"
                    size="small"
                    value={filters.createdBefore}
                    onChange={(e) => handleFilterChange('createdBefore', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Last Online After"
                    type="datetime-local"
                    size="small"
                    value={filters.onlineAfter}
                    onChange={(e) => handleFilterChange('onlineAfter', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Last Online Before"
                    type="datetime-local"
                    size="small"
                    value={filters.onlineBefore}
                    onChange={(e) => handleFilterChange('onlineBefore', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      {error && (
        <Box sx={{ p: 2, mb: 2, bgcolor: 'error.main', color: 'white', borderRadius: 1 }}>
          <Typography variant="body2">
            Error: {error.message}
          </Typography>
        </Box>
      )}
      
      {isLoading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          <Grid container spacing={2}>
            {users?.map((user) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={user.id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    background: 'linear-gradient(145deg, #1a1a1a 0%, #252525 100%)',
                    border: '1px solid rgba(0, 229, 255, 0.2)',
                    borderRadius: 2,
                    overflow: 'hidden',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      background: 'linear-gradient(90deg, #00e5ff 0%, #2196f3 50%, #00e5ff 100%)',
                      opacity: 0,
                      transition: 'opacity 0.3s ease',
                    },
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 40px rgba(0, 229, 255, 0.15)',
                      border: '1px solid rgba(0, 229, 255, 0.4)',
                      '&::before': {
                        opacity: 1,
                      }
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                    {/* User Header */}
                    <Box 
                      display="flex" 
                      alignItems="center" 
                      gap={2} 
                      mb={2}
                      sx={{ 
                        cursor: 'pointer',
                        borderRadius: 2,
                        p: 1.5,
                        mx: -1,
                        textDecoration: 'none',
                        color: 'inherit',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: 'rgba(0, 229, 255, 0.08)',
                          textDecoration: 'none',
                          transform: 'scale(1.02)',
                        },
                        '&:visited': {
                          color: 'inherit'
                        },
                        '&:link': {
                          color: 'inherit',
                          textDecoration: 'none'
                        }
                      }}
                      component="a"
                      href={`/user/${user.id}/details`}
                      onClick={(event) => selectUserById(user.id, navigate)}
                      title="Click to view user details (Ctrl+click for new tab)"
                    >
                      <Avatar 
                        src={`https://paradise.cloud/avatar/${user.id}.png`}
                        sx={{ 
                          width: 48, 
                          height: 48, 
                          bgcolor: 'primary.main',
                          background: 'linear-gradient(135deg, #00e5ff 0%, #2196f3 100%)',
                          fontWeight: 'bold',
                          fontSize: '1.2rem',
                          border: '2px solid rgba(0, 229, 255, 0.3)',
                          boxShadow: '0 2px 8px rgba(0, 229, 255, 0.2)',
                        }}
                      >
                        {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                      </Avatar>
                      <Box flexGrow={1} minWidth={0}>
                        <Typography variant="h6" noWrap>
                          {user.name || 'Unnamed User'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {user.email}
                        </Typography>
                        {user.tycoonLevel && (
                          <Typography variant="caption" color="primary.main" sx={{ fontWeight: 'bold' }}>
                            Tycoon Level {user.tycoonLevel}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Status Chips */}
                    {((user.banned && user.banned !== 0) || (user.cheater && user.cheater !== 0) || user.permaban) ? (
                      <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                        {user.banned && user.banned !== 0 && (
                          <Chip 
                            label="BANNED" 
                            color="error" 
                            size="small" 
                            sx={{ fontSize: '0.7rem', fontWeight: 'bold' }} 
                          />
                        )}
                        {user.cheater && user.cheater !== 0 && (
                          <Chip 
                            label="CHEATER" 
                            color="warning" 
                            size="small" 
                            sx={{ fontSize: '0.7rem', fontWeight: 'bold' }} 
                          />
                        )}
                        {user.permaban && (
                          <Chip 
                            label="PERMABAN" 
                            color="error" 
                            size="small" 
                            sx={{ 
                              fontSize: '0.7rem', 
                              fontWeight: 'bold',
                              bgcolor: 'error.dark',
                              color: 'error.contrastText',
                              '&:hover': {
                                bgcolor: 'error.main'
                              }
                            }} 
                          />
                        )}
                      </Box>
                    ) : null}

                    {/* User Details */}
                    <Stack spacing={1}>
                      {user.playerid && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Player ID:</strong> {user.playerid}
                        </Typography>
                      )}
                      {user.wallet && (
                        <Typography variant="body2" color="text.secondary" noWrap>
                          <strong>Wallet:</strong> {user.wallet.substring(0, 8)}...{user.wallet.substring(user.wallet.length - 6)}
                        </Typography>
                      )}
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#00e676',
                          fontWeight: 'bold',
                          background: 'linear-gradient(135deg, #00e676 0%, #00e5ff 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                        }}
                      >
                        <strong>MOANI:</strong> {formatMoani(user.moani)}
                      </Typography>
                      {user.createdAt && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Created:</strong> {new Date(user.createdAt).toLocaleDateString()}
                        </Typography>
                      )}
                      {user.online && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Last Online:</strong> {new Date(user.online).toLocaleDateString()}
                        </Typography>
                      )}
                    </Stack>
                  </CardContent>

                  <Divider />
                  
                  <CardActions sx={{ p: 1, justifyContent: 'space-between' }}>
                    <Button
                      size="small"
                      variant={user.banned && user.banned !== 0 ? 'contained' : 'outlined'}
                      color={user.banned && user.banned !== 0 ? 'error' : 'inherit'}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleBanned(user, e);
                      }}
                      disabled={updateUserStatusMutation.isLoading}
                      startIcon={user.banned && user.banned !== 0 ? <CheckCircleIcon /> : <BlockIcon />}
                      sx={{ minWidth: 70 }}
                    >
                      {user.banned && user.banned !== 0 ? 'Unban' : 'Ban'}
                    </Button>
                    <Button
                      size="small"
                      variant={user.permaban ? 'contained' : 'outlined'}
                      color={user.permaban ? 'error' : 'inherit'}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTogglePermaban(user, e);
                      }}
                      disabled={updateUserStatusMutation.isLoading}
                      startIcon={user.permaban ? <CheckCircleIcon /> : <SecurityIcon />}
                      sx={{ 
                        minWidth: 90,
                        bgcolor: user.permaban ? 'error.dark' : 'inherit',
                        '&:hover': {
                          bgcolor: user.permaban ? 'error.main' : 'inherit'
                        }
                      }}
                    >
                      {user.permaban ? 'Unban' : 'Permaban'}
                    </Button>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => handleOpenDeleteDialog(user, e)}
                      disabled={updateUserStatusMutation.isLoading}
                      sx={{ ml: 'auto' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* No users found */}
          {users?.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
              <Typography variant="h6" color="text.secondary">
                No users found
              </Typography>
            </Paper>
          )}
          
          {/* Load More Button */}
          {hasNextPage && !isFetchingNextPage && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Button 
                variant="outlined" 
                onClick={() => fetchNextPage()}
                sx={{ minWidth: 120 }}
              >
                Load More
              </Button>
            </Box>
          )}
          
          {/* Loading indicator for fetching more pages */}
          {isFetchingNextPage && (
            <Box display="flex" justifyContent="center" alignItems="center" mt={3}>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ ml: 1 }}>
                Loading more users...
              </Typography>
            </Box>
          )}
          
          {/* End of results indicator */}
          {!hasNextPage && users.length > 0 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Typography variant="body2" color="text.secondary">
                No more users to load
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Delete User Confirmation Dialog */}
      <UserDeletionDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setUserToDelete(null);
        }}
        user={userToDelete}
        onDeleteSuccess={handleDeleteSuccess}
      />
    </Box>
  );
}

export default UserSearch; 